import { Injectable } from '@angular/core';
import { PaymentService } from 'app/shared/services/claims-payments.service';
import { LoggerService } from 'app/shared/services/logger.service';
import { BankAccountDetails } from '../models/payment-preferences.model';

export interface AccountLoadResult {
    accounts: BankAccountDetails[];
    primaryAccount?: BankAccountDetails;
    hasAccountDetails: boolean;
}

export interface ReftAccountResult {
    hasReftAccount: boolean;
    accountDetails?: BankAccountDetails;
    billingAccountNumber?: string;
}

@Injectable()
export class PaymentAccountFacadeService {
    private static readonly LOG_PREFIX = 'Payment Account Facade';
    private static readonly REFT_ACCOUNT_KEY = 'reftAccount';

    constructor(
        private readonly paymentService: PaymentService,
        private readonly loggerService: LoggerService
    ) {}

    async loadBankAccountDetails(claimNumber: string, contactPublicId: string): Promise<AccountLoadResult> {
        try {
            this.loggerService.info(`${PaymentAccountFacadeService.LOG_PREFIX} - Loading Account Details`, {
                claimNumber,
                contactPublicId
            });

            const details = await this.paymentService.getBankAccountDetails(claimNumber, contactPublicId);

            if (!this.hasValidAccountDetails(details)) {
                this.loggerService.info(`${PaymentAccountFacadeService.LOG_PREFIX} - No Account Details Found`);
                return { accounts: [], hasAccountDetails: false };
            }

            const accountList = this.processAccountDetails(details);

            this.loggerService.info(`${PaymentAccountFacadeService.LOG_PREFIX} - Account Details Loaded Successfully`, {
                accountCount: accountList.length
            });

            return {
                accounts: accountList,
                primaryAccount: accountList[0],
                hasAccountDetails: true
            };
        } catch (error) {
            this.loggerService.error(`${PaymentAccountFacadeService.LOG_PREFIX} - Error in getBankAccountDetails`, { error });
            return { accounts: [], hasAccountDetails: false };
        }
    }

    async loadReftAccount(policyNumber: string): Promise<ReftAccountResult> {
        try {
            this.loggerService.info(`${PaymentAccountFacadeService.LOG_PREFIX} - Loading REFT Info`, { policyNumber });

            const response = await this.paymentService.getAgreementFinancialInfo(policyNumber);

            if (response?.paymentMethod === 'EFT') {
                const reftAccountDetails = this.mapToReftAccountDetails(response);
                return {
                    hasReftAccount: true,
                    accountDetails: reftAccountDetails,
                    billingAccountNumber: response.billingAccountNumber
                };
            } else {
                this.loggerService.info(`${PaymentAccountFacadeService.LOG_PREFIX} - No REFT Account`, {
                    paymentMethod: response?.paymentMethod
                });
                return { hasReftAccount: false };
            }
        } catch (error) {
            this.loggerService.error(`${PaymentAccountFacadeService.LOG_PREFIX} - Error in getAgreementFinancialInfo`, {
                error,
                policyNumber
            });
            return { hasReftAccount: false };
        }
    }

    async validateAndFetchBankDetails(routingNumber: string): Promise<string> {
        try {
            const response = await this.paymentService.getBankDetails(routingNumber);
            return response.fullName || '';
        } catch (error) {
            this.loggerService.error(`${PaymentAccountFacadeService.LOG_PREFIX} - Error in getBankDetails`, {
                error,
                routingNumber
            });
            throw error;
        }
    }

    private hasValidAccountDetails(details: any): boolean {
        return !!(details && (!Array.isArray(details) || details.length > 0));
    }

    private processAccountDetails(details: any): BankAccountDetails[] {
        const accountList = Array.isArray(details) ? details : [details];
        const mappedAccounts = accountList.map((account: any) => this.mapResponseAccount(account));
        return this.sortAccountsByPrimary(mappedAccounts);
    }

    private sortAccountsByPrimary(accounts: BankAccountDetails[]): BankAccountDetails[] {
        const primaryIndex = accounts.findIndex((account) => account.primaryAccount);

        if (primaryIndex <= 0) {
            return accounts;
        }

        const [primary] = accounts.splice(primaryIndex, 1);
        return [primary, ...accounts];
    }

    private mapToReftAccountDetails(response: any): BankAccountDetails {
        return {
            publicID: PaymentAccountFacadeService.REFT_ACCOUNT_KEY,
            accountName: 'Automatic Payment Account',
            bankRoutingNumber: response.bankRoutingNumber || '',
            bankAccountNumber: response.maskedBankAccountNumber || response.bankAccountNumber || '',
            bankName: response.bankName || '',
            bankAccountType: response.bankAccountType ?? response.bankAccountingType,
            primaryAccount: false
        };
    }

    private mapResponseAccount(apiAccount: any): BankAccountDetails {
        return {
            publicID: apiAccount.publicID,
            accountName: apiAccount.accountName,
            bankRoutingNumber: apiAccount.bankRoutingNumber,
            bankAccountNumber: apiAccount.maskedBankAccountNumber || apiAccount.bankAccountNumber,
            bankName: apiAccount.bankName,
            bankAccountType: apiAccount.bankAccountType ?? apiAccount.bankAccountingType,
            primaryAccount: apiAccount.primaryAccount
        };
    }

    async saveBankAccount(details: {
        bankRoutingNumber: string;
        accountName: string;
        bankAccountNumber: string;
        bankName: string;
        bankAccountingType: string;
        primaryAccount: boolean;
        contactID: string;
        claimNumber: string;
    }): Promise<BankAccountDetails[]> {
        const payload = {
            bankRoutingNumber: details.bankRoutingNumber,
            accountName: details.accountName,
            bankAccountNumber: details.bankAccountNumber,
            bankName: details.bankName,
            bankAccountingType: details.bankAccountingType,
            primaryAccount: details.primaryAccount,
            contactID: details.contactID,
            claimNumber: details.claimNumber
        };

        try {
            this.loggerService.info(`${PaymentAccountFacadeService.LOG_PREFIX} - Saving Bank Account`, {
                bankAccountingType: details.bankAccountingType,
                accountName: details.accountName
            });

            const response = await this.paymentService.updateBankAccountDetails(payload);
            this.loggerService.info(`${PaymentAccountFacadeService.LOG_PREFIX} - updateBankAccountDetails Success`);

            return this.processAccountDetails(response);
        } catch (error) {
            this.loggerService.error(`${PaymentAccountFacadeService.LOG_PREFIX} - Error in updateBankAccountDetails`, { error });
            throw error;
        }
    }

    async saveReftAccount(claimDetails: any): Promise<void> {
        try {
            this.loggerService.info(`${PaymentAccountFacadeService.LOG_PREFIX} - Saving REFT Account`, { claimDetails });
            await this.paymentService.saveReftAccountDetails(claimDetails);
        } catch (error) {
            this.loggerService.error(`${PaymentAccountFacadeService.LOG_PREFIX} - Error in saveReftAccountDetails`, { error });
            throw error;
        }
    }
}
